/**
 * Dashboard Plugin System - Component Helper
 * 
 * Vue mixin/helper for integrating tab resolution in components.
 * Provides easy-to-use methods for tab management with plugin support.
 */

import { getTabResolver } from './dashboard-tab-resolver';

export default {
  data() {
    return {
      // Store component name for plugin system
      pluginComponentName: 'Settings', // Override in component if needed
    };
  },

  methods: {
    /**
     * Get plugin context for current component
     * NOTE: Components should define their own pluginContext() method
     * This is a fallback that provides basic context
     */
    getPluginContext() {
      // If component defines pluginContext as computed or method, use it
      if (typeof this.pluginContext === 'function') {
        return this.pluginContext();
      }
      if (this.pluginContext && typeof this.pluginContext === 'object') {
        return this.pluginContext;
      }
      
      // Fallback: try to get context from common properties
      const context = { inbox: this.inbox || null };
      
      // Safely check for user/account in $store
      if (this.$store) {
        const state = this.$store.state;
        if (state.auth && state.auth.currentUser) {
          context.user = state.auth.currentUser;
        }
        if (state.accounts && state.accounts.currentAccount) {
          context.account = state.accounts.currentAccount;
        }
      }
      
      return context;
    },

    /**
     * Resolve tabs with plugin system
     * @param {Array} defaultTabs - Default tabs for the component
     * @returns {Array} Resolved tabs with plugins applied
     */
    resolvePluginTabs(defaultTabs) {
      const resolver = getTabResolver();
      const componentName = this.pluginComponentName || 'Settings';
      
      return resolver.resolve(componentName, defaultTabs, this.getPluginContext());
    },

    /**
     * Get component for a specific tab (with plugin override support)
     * @param {string} tabKey - Tab key to get component for
     * @returns {any} Component to render (override or default)
     */
    getTabComponent(tabKey) {
      const resolver = getTabResolver();
      const componentName = this.pluginComponentName || 'Settings';
      
      return resolver.getContentForTab(componentName, tabKey, this.getPluginContext());
    },

    /**
     * Check if tab should be overridden by plugin
     * @param {string} tabKey - Tab key to check
     * @returns {boolean} True if plugin provides override
     */
    hasTabOverride(tabKey) {
      return this.getTabComponent(tabKey) !== null;
    },

    /**
     * Check if tab is visible (not hidden by plugins)
     * @param {string} tabKey - Tab key to check
     * @param {Array} resolvedTabs - Already resolved tabs
     * @returns {boolean} True if tab is visible
     */
    isTabVisible(tabKey, resolvedTabs) {
      const tabs = resolvedTabs || [];
      const tab = tabs.find(t => t.key === tabKey);
      return tab ? !tab.hidden : false;
    },
  },
};
